/**
 * Shared client-side field validators — mirrors apps/api/src/lib/field-validators.ts.
 * Keep the two in sync: the API is the source of truth, this gives instant UX feedback.
 *
 * Rules (updated 2026-06-18):
 *  - Person / party / company NAME: letters (incl. accented Latin), spaces,
 *    hyphens, apostrophes, periods only. Must contain at least one letter.
 *    `sanitizeName` strips anything else so numbers/symbols can't be typed at all.
 *  - PHONE: per-country length (2026-10-06). The country comes from the <PhoneInput>
 *    dropdown, so the national number is checked against THAT country — the United
 *    Kingdom is 10 digits after +44 (12 in total). Table: `phone-rules.ts`.
 */
import { isValidPhoneNumber } from './phone-rules';

const NAME_ALLOWED = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ'’.\-\s]+$/;
const NAME_DISALLOWED = /[^A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ'’.\-\s]/g;
const HAS_LETTER = /[A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ]/;

// UK legal MATTER / CASE name rule — MIRRORS apps/api/src/lib/field-validators.ts
// (MATTER_NAME_*). Keep the two IDENTICAL. Real matter names use digits, & ( ) [ ] : ; / , .
// apostrophes/quotes and en/em dashes — e.g. "Smith & Co v Jones", "R v Brown [2024] EWCA".
// Distinct from the strict person-name rule above (still used for party/person names).
const MATTER_NAME_ALLOWED = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ0-9 .,:;'’"“”()\[\]&/–—-]+$/;
const MATTER_NAME_DISALLOWED = /[^A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ0-9 .,:;'’"“”()\[\]&/–—-]/g;

// Permissive ENTITY name (companies, firms, opposing parties) — MIRRORS
// ENTITY_ALLOWED in apps/api/src/lib/field-validators.ts. Keep the two IDENTICAL.
// A party or a solicitor is as often an organisation as a person, so "Harker & Vance
// LLP" and "3M Ltd" must pass where the strict person rule above would reject them.
const ENTITY_NAME_ALLOWED = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ0-9'’.,&()/\-\s]+$/;

export const NAME_ERROR = 'Only letters, spaces, hyphens, apostrophes and periods are allowed';
export const MATTER_NAME_ERROR = "A matter name may use letters, numbers, spaces and & ( ) [ ] : ; , . ' \" / and dashes";
export const ENTITY_NAME_ERROR = "A name may use letters, numbers, spaces and & ( ) / , . ' and dashes";
export const PHONE_ERROR = 'Enter a valid phone number including country code';
export const EMAIL_ERROR = 'Enter a valid email address';
/** MIRRORS NAME_LETTER_MSG in apps/api/src/lib/field-validators.ts. */
export const NAME_LETTER_ERROR = 'Must contain at least one letter';
export const TITLE_ERROR = 'Enter a title that describes the task — at least 3 characters including a letter';

/** Shortest title that can say anything. MIRRORS the API. */
export const MIN_TITLE_LENGTH = 3;

/**
 * The precise reason an entity name is unusable, or '' when it is fine.
 *
 * ENTITY_NAME_ERROR alone was misleading for a digits-only value like "1212321321323": it lists
 * numbers among the allowed characters, so it reads as "numbers are allowed" while rejecting a
 * name made only of them. The API already separates the two refinements
 * (field-validators.ts:120-121); this says the same thing on the client.
 */
export function entityNameError(value: string): string {
  const t = value.trim();
  if (!t) return '';
  if (!ENTITY_NAME_ALLOWED.test(t)) return ENTITY_NAME_ERROR;
  if (!HAS_LETTER.test(t)) return NAME_LETTER_ERROR;
  return '';
}

/** Valid person/company name (non-empty, ≥1 letter, allowed chars only). */
export function isValidName(value: string): boolean {
  const t = value.trim();
  return t.length > 0 && NAME_ALLOWED.test(t) && HAS_LETTER.test(t);
}

/**
 * Strip every character that isn't allowed in a name (digits, @, #, etc.).
 * Use in an input's onChange so the user physically cannot type invalid chars.
 */
export function sanitizeName(value: string): string {
  return value.replace(NAME_DISALLOWED, '');
}

/** Valid UK matter/case name (non-empty, ≥1 letter, matter-name chars only). */
export function isValidMatterName(value: string): boolean {
  const t = value.trim();
  return t.length > 0 && MATTER_NAME_ALLOWED.test(t) && HAS_LETTER.test(t);
}

/**
 * Strip only characters NOT permitted in a matter name (control chars, <, >, @, #, …).
 * Input hygiene for immediate UX — NOT a substitute for output escaping.
 */
export function sanitizeMatterName(value: string): string {
  return value.replace(MATTER_NAME_DISALLOWED, '');
}

/**
 * Valid ENTITY name — a company, firm or person (non-empty, ≥1 letter, entity chars only).
 * Mirrors `entityNameSchema` on the API. Use for party and solicitor names, NOT for the
 * strict person-name fields (those keep `isValidName`).
 */
export function isValidEntityName(value: string): boolean {
  const t = value.trim();
  return t.length > 0 && ENTITY_NAME_ALLOWED.test(t) && HAS_LETTER.test(t);
}

/** Keep only digits (for the national-number part of a phone input). */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Valid phone for the country its dial code names — see `phone-rules.ts`.
 *
 * Was "optional +, 8–15 digits" worldwide, which accepted a half-typed UK number
 * ("+44 791112"). The country comes from a dropdown, so the length is checked against
 * that country: the United Kingdom is 10 digits after +44, i.e. 12 in total.
 */
export function isValidPhone(value: string): boolean {
  return isValidPhoneNumber(value.trim());
}

/**
 * A title a human actually typed — used for task titles.
 *
 * Mirrors `meaningfulTitleSchema` on the API. A title is free text (real ones carry digits
 * and punctuation: "Draft skeleton for 3 Oct", "Review CPR 31.16"), so this cannot be a
 * character whitelist. What it rejects is input that carries no information: one character
 * held down ("11111111111", "aaaaaaa"), punctuation only, or two characters.
 */
export function isMeaningfulTitle(value: string): boolean {
  const t = value.trim();
  if (t.length < MIN_TITLE_LENGTH) return false;
  if (!HAS_LETTER.test(t)) return false;
  // One repeated character, ignoring spaces — "aaa aaa aaa" is as empty as "aaaaaaaaa".
  const compact = t.replace(/\s/g, '');
  if (new Set(compact).size === 1) return false;
  return true;
}

/** Standard email. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * A joining link for a virtual hearing — http(s) only.
 *
 * Not mirrored from the API, which caps `videoLink` at 500 characters but does not check its
 * shape. The check lives here because counsel clicks this link on a court morning: "TBC", a
 * phone number or a room name is found out at 10:29, not at 10:30.
 */
export function isMeetingLink(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
